import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { timer, Observable, share, interval, switchMap } from 'rxjs';

import { Nullable } from '../helpers/types';

import { CAPTCHA_CONFIG } from './injection-tokens';
import { CaptchaAPIConfig, CaptchaResponseData } from './types';


@Injectable({
    providedIn: 'root'
})
export class CaptchService {

    /** HTTP client DI. */
    private _httpClient: HttpClient = inject(HttpClient);

    /** Captcha API config DI. */
    readonly captchaConfig: CaptchaAPIConfig = inject(CAPTCHA_CONFIG);

    /** Time remaining. */
    timeRemainingSgl: WritableSignal<number> = signal(this.captchaConfig.timeout_sec);

    /** Captcha data. */
    private _currentCaptchaDataSgl: WritableSignal<Nullable<CaptchaResponseData>> = signal(null);

    /** Captcha service. */
    constructor() {
        // Setup captcha automatic generation.
        // TODO What about error ? Is observable terminated ? If so, put this in a method and call it on regenerateCaptcha.
        timer(0, this.captchaConfig.timeout_sec * 1000).pipe(
            switchMap(() => this._getCaptcha()),
            share(),
            takeUntilDestroyed()
        ).subscribe({
            next: (captcha: CaptchaResponseData) => this._currentCaptchaDataSgl.set(captcha),
            error: () => this._currentCaptchaDataSgl.set(null)
        });

        // Update timer.
        interval(200).pipe(takeUntilDestroyed()).subscribe({
            next: (timer: number) => this.timeRemainingSgl.update(
                (v: number) => {
                    if (v <= 0 || !this.captcha) {
                        return this.captchaConfig.timeout_sec;
                    }
                    return v - 0.2;
                }
            )
        });
    }

    /** Get captcha. */
    private _getCaptcha(): Observable<CaptchaResponseData> {
        this._currentCaptchaDataSgl.set(null);
        return this._httpClient.get<CaptchaResponseData>(`${this.captchaConfig.apiUrl}/generate`);
    }

    /** Regenerate captcha manually. */
    regenerateCaptcha(): void {
        this._getCaptcha().subscribe({
            next: (captcha: CaptchaResponseData) => this._currentCaptchaDataSgl.set(captcha),
            error: () => this._currentCaptchaDataSgl.set(null)
        });
    }

    /** Verify captcha. */
    verifyCaptcha(userInput: string): Observable<Partial<CaptchaResponseData>> {
        return this._httpClient.post<CaptchaResponseData>(
            `${this.captchaConfig.apiUrl}/verify`,
            { captchaId: this._currentCaptchaDataSgl()?.captchaId ?? '', userInput }
        );
    }

    /** Get captcha text. */
    get captcha(): Nullable<string> {
        return this._currentCaptchaDataSgl()?.captchaText;
    }

    /** Get time remaining percentage. */
    get timeRemainingPercent(): number {
        return this.timeRemainingSgl() / this.captchaConfig.timeout_sec * 100;
    }
}
